import React, { useState, useEffect, useCallback } from 'react';
import ApiClient, {
  CostOverview,
  CostHistoryResponse,
  CostBreakdownResponse,
  CostBreakdownItem,
  CostHistoryDay,
} from '../api/client';
import { LocalTabs, PageHeader } from './ui';

interface CostAnalyticsProps {
  apiClient: ApiClient;
}

type CostTab = 'overview' | 'history' | 'breakdown';
type PeriodKey = 'daily' | 'weekly' | 'monthly' | 'quarterly' | 'annual';
type CostHistoryMode = 'daily' | 'weekly' | 'monthly';

interface CostHistoryBucket {
  key: string;
  label: string;
  rangeLabel: string;
  totalCost: number;
  requestCount: number;
}

const periodKeys: PeriodKey[] = ['daily', 'weekly', 'monthly', 'quarterly', 'annual'];

export const formatCurrency = (value: number, decimals: number = 2): string => {
  if (value < 0.01 && value > 0) {
    return `$${value.toFixed(4)}`;
  }
  return `$${value.toFixed(decimals)}`;
};

const formatTokens = (value: number): string => {
  if (value >= 1000000) {
    return `${(value / 1000000).toFixed(1)}M`;
  }
  if (value >= 1000) {
    return `${(value / 1000).toFixed(1)}K`;
  }
  return value.toString();
};

const formatShortDate = (date: string): string =>
  new Date(`${date}T00:00:00`).toLocaleDateString(undefined, {
    month: 'short',
    day: 'numeric',
  });

const formatMonthLabel = (date: string): string =>
  new Date(`${date}T00:00:00`).toLocaleDateString(undefined, {
    month: 'short',
    year: 'numeric',
  });

const getTickInterval = (count: number): number => {
  if (count <= 14) return 1;
  if (count <= 30) return 5;
  if (count <= 60) return 7;
  return 14;
};

export const getHistoryMode = (days: number): CostHistoryMode => {
  if (days >= 365) return 'monthly';
  if (days >= 90) return 'weekly';
  return 'daily';
};

const getHistoryTitle = (mode: CostHistoryMode): string => {
  if (mode === 'monthly') return 'Monthly cost history';
  if (mode === 'weekly') return 'Weekly cost history';
  return 'Daily cost history';
};

const getHistoryWindowLabel = (days: number, mode: CostHistoryMode): string => {
  if (mode === 'monthly') return '12 month window';
  if (mode === 'weekly') return `${days} day window, grouped weekly`;
  return `${days} day window`;
};

const getAverageLabel = (mode: CostHistoryMode): string => {
  if (mode === 'monthly') return 'Avg/month';
  if (mode === 'weekly') return 'Avg/week';
  return 'Avg/day';
};

export const aggregateHistory = (days: CostHistoryDay[], mode: CostHistoryMode): CostHistoryBucket[] => {
  if (mode === 'daily') {
    return days.map((day) => ({
      key: day.date,
      label: formatShortDate(day.date),
      rangeLabel: day.date,
      totalCost: parseFloat(day.total_cost),
      requestCount: day.request_count,
    }));
  }

  if (mode === 'weekly') {
    const buckets: CostHistoryBucket[] = [];
    for (let index = 0; index < days.length; index += 7) {
      const week = days.slice(index, index + 7);
      const first = week[0];
      const last = week[week.length - 1];
      if (!first || !last) continue;
      const sameMonth = first.date.slice(5, 7) === last.date.slice(5, 7);
      buckets.push({
        key: first.date,
        label: sameMonth
          ? `${formatShortDate(first.date)}-${new Date(`${last.date}T00:00:00`).getDate()}`
          : `${formatShortDate(first.date)}-${formatShortDate(last.date)}`,
        rangeLabel: `${first.date} to ${last.date}`,
        totalCost: week.reduce((sum, day) => sum + parseFloat(day.total_cost), 0),
        requestCount: week.reduce((sum, day) => sum + day.request_count, 0),
      });
    }
    return buckets;
  }

  const monthMap = new Map<string, CostHistoryBucket>();
  days.forEach((day) => {
    const key = day.date.slice(0, 7);
    const existing = monthMap.get(key);
    if (existing) {
      existing.totalCost += parseFloat(day.total_cost);
      existing.requestCount += day.request_count;
      existing.rangeLabel = `${existing.rangeLabel.split(' to ')[0]} to ${day.date}`;
    } else {
      monthMap.set(key, {
        key,
        label: formatMonthLabel(`${key}-01`),
        rangeLabel: day.date,
        totalCost: parseFloat(day.total_cost),
        requestCount: day.request_count,
      });
    }
  });
  return Array.from(monthMap.values());
};

const formatChange = (change: number | null): { text: string; tone: string } => {
  if (change === null) {
    return { text: 'No comparison', tone: 'neutral' };
  }
  if (change > 0) {
    return { text: `+${change.toFixed(1)}%`, tone: 'danger' };
  }
  if (change < 0) {
    return { text: `${change.toFixed(1)}%`, tone: 'success' };
  }
  return { text: '0%', tone: 'neutral' };
};

const CostMetric: React.FC<{
  label: string;
  value: string;
  tone?: 'brand' | 'success' | 'info' | 'warning' | 'neutral';
}> = ({ label, value, tone = 'brand' }) => (
  <div className={`cost-metric-card ${tone}`}>
    <span>{label}</span>
    <strong>{value}</strong>
  </div>
);

const BreakdownTable: React.FC<{
  title: string;
  label: string;
  rows: CostBreakdownItem[] | undefined;
  mode: 'service' | 'model';
}> = ({ title, label, rows, mode }) => (
  <section className="cost-table-card">
    <div className="cost-section-header">
      <h3>{title}</h3>
      <span>{rows?.length ?? 0} rows</span>
    </div>
    {rows && rows.length > 0 ? (
      <div className="cost-table-wrap">
        <table className="cost-table">
          <thead>
            <tr>
              <th>{label}</th>
              <th className="right">Cost</th>
              <th className="right">Requests</th>
              {mode === 'model' && <th className="right">Avg</th>}
            </tr>
          </thead>
          <tbody>
            {rows.map((item, index) => {
              const totalCost = parseFloat(item.total_cost);
              const avgCost = item.request_count > 0 ? totalCost / item.request_count : 0;
              return (
                <tr key={`${item.service ?? item.model ?? 'unknown'}-${index}`}>
                  <td className={mode === 'model' ? 'mono' : undefined}>
                    {mode === 'model' ? item.model || 'Unknown' : item.service || 'Unknown'}
                  </td>
                  <td className="right strong">{formatCurrency(totalCost)}</td>
                  <td className="right">{item.request_count.toLocaleString()}</td>
                  {mode === 'model' && <td className="right accent">{formatCurrency(avgCost, 4)}</td>}
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>
    ) : (
      <div className="empty-state">
        <p>No {label.toLowerCase()} breakdown available</p>
      </div>
    )}
  </section>
);

const CostAnalytics: React.FC<CostAnalyticsProps> = ({ apiClient }) => {
  const [overview, setOverview] = useState<CostOverview | null>(null);
  const [history, setHistory] = useState<CostHistoryResponse | null>(null);
  const [byService, setByService] = useState<CostBreakdownResponse | null>(null);
  const [byModel, setByModel] = useState<CostBreakdownResponse | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [historyDays, setHistoryDays] = useState(30);
  const [activeTab, setActiveTab] = useState<CostTab>('overview');

  const loadData = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const [overviewData, historyData, serviceData, modelData] = await Promise.all([
        apiClient.getCostOverview(),
        apiClient.getCostHistory(historyDays),
        apiClient.getCostByService(),
        apiClient.getCostByModel(),
      ]);
      setOverview(overviewData);
      setHistory(historyData);
      setByService(serviceData);
      setByModel(modelData);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to load cost data');
    } finally {
      setLoading(false);
    }
  }, [apiClient, historyDays]);

  useEffect(() => {
    loadData();
  }, [loadData]);

  const getFilledHistory = (): CostHistoryDay[] => {
    if (!history || !history.history.length) return [];

    const dataMap = new Map<string, CostHistoryDay>();
    history.history.forEach((day) => dataMap.set(day.date, day));

    const result: CostHistoryDay[] = [];
    const startDate = new Date(history.start_date);
    const endDate = new Date(history.end_date);

    for (let day = new Date(startDate); day <= endDate; day.setDate(day.getDate() + 1)) {
      const dateStr = day.toISOString().split('T')[0];
      const existing = dataMap.get(dateStr);
      result.push(
        existing ?? {
          date: dateStr,
          total_cost: '0',
          total_input_tokens: '0',
          total_output_tokens: '0',
          request_count: 0,
        }
      );
    }
    return result;
  };

  const filledHistory = getFilledHistory();
  const historyMode = getHistoryMode(historyDays);
  const chartHistory = aggregateHistory(filledHistory, historyMode);
  const maxCost = Math.max(...chartHistory.map((bucket) => bucket.totalCost), 0.01);
  const historyTotal = chartHistory.reduce((sum, bucket) => sum + bucket.totalCost, 0);
  const historyRequests = chartHistory.reduce((sum, bucket) => sum + bucket.requestCount, 0);
  const historyAverage = chartHistory.length > 0 ? historyTotal / chartHistory.length : 0;
  const peakBucket = chartHistory.reduce<CostHistoryBucket | null>((peak, bucket) => {
    if (!peak || bucket.totalCost > peak.totalCost) return bucket;
    return peak;
  }, null);
  const tickInterval = getTickInterval(chartHistory.length);
  const allTime = overview?.all_time;
  const avgCost =
    allTime && allTime.total_requests > 0
      ? allTime.total_cost_usd / allTime.total_requests
      : 0;

  if (loading && !overview) {
    return (
      <div className="manager-page costs-page">
        <div className="empty-state">
          <div className="loading-spinner"></div>
          <p>Loading cost analytics...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="manager-page costs-page">
      {error && (
        <div className="alert alert-error">
          <span>{error}</span>
          <button className="btn btn-secondary btn-sm" onClick={() => setError(null)}>
            Dismiss
          </button>
        </div>
      )}

      <PageHeader
        title="Costs"
        actions={
          <>
          <select
            className={`cost-range-select ${activeTab !== 'history' ? 'is-hidden' : ''}`}
            value={historyDays}
            onChange={(event) => setHistoryDays(parseInt(event.target.value, 10))}
            disabled={activeTab !== 'history'}
            aria-hidden={activeTab !== 'history'}
            tabIndex={activeTab === 'history' ? 0 : -1}
          >
            <option value={7}>Last 7 days</option>
            <option value={14}>Last 14 days</option>
            <option value={30}>Last 30 days</option>
            <option value={60}>Last 60 days</option>
            <option value={90}>Last 90 days</option>
            <option value={365}>Last 12 months</option>
          </select>
          <button className="btn btn-primary" onClick={loadData} disabled={loading}>
            Refresh
          </button>
          </>
        }
      />

      <div className="costs-shell">
        <LocalTabs
          tabs={[
            { id: 'overview', label: 'Overview' },
            { id: 'history', label: 'History' },
            { id: 'breakdown', label: 'Breakdown' },
          ]}
          active={activeTab}
          onChange={setActiveTab}
        />

        <div className="costs-tab-panel">
          {activeTab === 'overview' && (
            <div className="costs-overview">
              {allTime && (
                <section className="cost-summary-panel">
                  <div className="cost-metric-grid">
                    <CostMetric label="All-time cost" value={formatCurrency(allTime.total_cost_usd)} />
                    <CostMetric label="Input tokens" value={formatTokens(allTime.total_input_tokens)} tone="success" />
                    <CostMetric label="Output tokens" value={formatTokens(allTime.total_output_tokens)} tone="info" />
                    <CostMetric label="Requests" value={allTime.total_requests.toLocaleString()} tone="neutral" />
                    <CostMetric label="Avg/request" value={formatCurrency(avgCost, 4)} tone="warning" />
                  </div>
                </section>
              )}

              <section className="cost-period-panel">
                <div className="cost-section-header">
                  <h3>Cost by period</h3>
                </div>
                <div className="cost-period-grid">
                  {overview &&
                    periodKeys.map((period) => {
                      const data = overview[period];
                      if (!data?.current) return null;
                      const change = formatChange(data.current.change_from_previous);
                      return (
                        <div className="cost-period-card" key={period}>
                          <div>
                            <span>{data.current_label}</span>
                            <strong>{formatCurrency(data.current.total_cost_usd)}</strong>
                          </div>
                          <div className="cost-period-meta">
                            <span className={`cost-change ${change.tone}`}>{change.text}</span>
                            <span>{data.current.request_count.toLocaleString()} requests</span>
                          </div>
                        </div>
                      );
                    })}
                </div>
              </section>
            </div>
          )}

          {activeTab === 'history' && (
            <section className="cost-chart-card">
              <div className="cost-section-header">
                <h3>{getHistoryTitle(historyMode)}</h3>
                <span>{getHistoryWindowLabel(historyDays, historyMode)}</span>
              </div>
              {chartHistory.length > 0 ? (
                <div className="cost-history-content">
                  <div className="cost-chart-stats">
                    <div>
                      <span>Total</span>
                      <strong>{formatCurrency(historyTotal)}</strong>
                    </div>
                    <div>
                      <span>{getAverageLabel(historyMode)}</span>
                      <strong>{formatCurrency(historyAverage, 4)}</strong>
                    </div>
                    <div>
                      <span>Peak</span>
                      <strong>{peakBucket ? `${formatCurrency(peakBucket.totalCost, 4)} ${peakBucket.label}` : '$0.00'}</strong>
                    </div>
                    <div>
                      <span>Requests</span>
                      <strong>{historyRequests.toLocaleString()}</strong>
                    </div>
                  </div>

                  <div className="cost-history-plot">
                    <div className="cost-y-axis">
                      <span>{formatCurrency(maxCost, 4)}</span>
                      <span>$0</span>
                    </div>
                    <div className="cost-chart-grid">
                      <div className="cost-chart-lines" aria-hidden="true">
                        <i />
                        <i />
                        <i />
                      </div>
                      <div className="cost-history-chart">
                        {chartHistory.map((bucket, index) => {
                          const height = maxCost > 0 ? (bucket.totalCost / maxCost) * 100 : 0;
                          const showTick =
                            index === 0 ||
                            index === chartHistory.length - 1 ||
                            index % tickInterval === 0;
                          return (
                            <div
                              className="cost-history-day"
                              key={bucket.key}
                              title={`${bucket.rangeLabel}: ${formatCurrency(bucket.totalCost, 4)} (${bucket.requestCount} requests)`}
                            >
                              <div
                                className={`cost-bar ${bucket.totalCost === 0 ? 'muted' : ''}`}
                                style={{ height: `${bucket.totalCost > 0 ? Math.max(height, 3) : 2}%` }}
                              />
                              <span className={showTick ? 'visible' : ''}>{showTick ? bucket.label : ''}</span>
                            </div>
                          );
                        })}
                      </div>
                    </div>
                  </div>
                </div>
              ) : (
                <div className="empty-state">
                  <p>No cost history available</p>
                </div>
              )}
            </section>
          )}

          {activeTab === 'breakdown' && (
            <div className="cost-breakdown-grid">
              <BreakdownTable title="Cost by service" label="Service" rows={byService?.breakdown} mode="service" />
              <BreakdownTable title="Cost by model" label="Model" rows={byModel?.breakdown} mode="model" />
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default CostAnalytics;
